import '__common/pixiCanvasFallback';
import * as PIXI from 'pixi.js';
import { autoDetectRenderer, IRenderer } from 'pixi.js';
import { default as CanvasLayer } from '__common/vendor/canvasLayer';
/* tslint:disable */

// CanvasRenderer is registered in pixiCanvasFallback (not pixi.js-legacy).
// Retry with forceCanvas when WebGL detection passes but construction throws
// (common when Google Maps has already taken a WebGL context).
function createOverlayRenderer(options: Parameters<typeof autoDetectRenderer>[0]) {
    try {
        return autoDetectRenderer(options);
    } catch (_) {
        return autoDetectRenderer({ ...options, forceCanvas: true });
    }
}

export default class PixiLayer {
    map: google.maps.Map;
    options: any;
    renderer: (IRenderer & { destroyed?: boolean }) | null;
    stage: PIXI.Container;
    canvasLayer: any;
    prevHeight: number;
    prevWidth: number;
    ratio: number;

    constructor(map, options = {}) {
        this.map = map;

        this.options = {
            animate: true,
            resolutionScale: 1,
            interactive: true,
            paneName: 'overlayMouseTarget',
            antialias: true,
            resolution: 1,
            ...options,
        }

        function updateHandler() {
            // var mapProjection = this.map.getProjection();
            // var scale = Math.pow(2, this.map.zoom) * pixiLayer.options.resolutionScale;
            // var offset = mapProjection.fromLatLngToPoint(this.getTopLeft());

            // pixiLayer.stage.position.x = -offset.x * scale;
            // pixiLayer.stage.position.y = -offset.y * scale;

            // pixiLayer.stage.scale.x = scale;
            // pixiLayer.stage.scale.y = scale;

            if (pixiLayer.renderer && pixiLayer.stage && !pixiLayer.renderer.destroyed) {
                try {
                    pixiLayer.renderer.render(pixiLayer.stage);
                } catch (error) {
                    console.warn('PIXI renderer error:', error);
                    // Optionally recreate renderer if it's corrupted
                    pixiLayer.recreateRenderer();
                }
            }
        }

        function resizeHandler() {
            if (this.canvas && pixiLayer.renderer) {
                pixiLayer.stage.hitArea = new PIXI.Rectangle(0, 0, this.canvas.width, this.canvas.height);
                pixiLayer.renderer.resize(this.canvas.width, this.canvas.height);
            }
        }


        const pixiLayer = this;
        updateHandler.bind(pixiLayer);
        resizeHandler.bind(pixiLayer);

        this.canvasLayer = new CanvasLayer({
            map: map,
            animate: this.options.animate,
            resolutionScale: this.options.resolutionScale,
            paneName: this.options.paneName,
            resizeHandler: resizeHandler,
            updateHandler: updateHandler,
        });

        try {
            this.renderer = createOverlayRenderer({
                width: this.canvasLayer.canvas.width,
                height: this.canvasLayer.canvas.height,
                view: this.canvasLayer.canvas,
                ...options,
                antialias: true,
                backgroundAlpha: 0
            });

            this.stage = new PIXI.Container();
            this.stage.interactive = true;
            this.stage.sortableChildren = true;
            this.stage.hitArea = new PIXI.Rectangle(0, 0, this.canvasLayer.canvas.width, this.canvasLayer.canvas.height);
            this.stage.interactiveChildren = true;
            this.prevHeight;
            this.prevWidth;
            this.ratio;

            // Initial render with error handling
            if (this.renderer && this.stage && !this.renderer.destroyed) {
                this.renderer.render(this.stage);
            }
        } catch (error) {
            console.error('Failed to initialize PIXI renderer:', error);
            this.renderer = null;
            this.stage = new PIXI.Container();
        }
    }

    addLayer = (drawFunction) => {
        if (!this.renderer || !this.stage || this.renderer.destroyed) return;
        
        var layer = new PIXI.Container();
        layer.interactive = this.options.interactive || false;
        let mapProjection = this.map.getProjection();
        drawFunction(layer, mapProjection);
        this.stage.addChild(layer);
        
        try {
            this.renderer.render(this.stage);
        } catch (error) {
            console.warn('PIXI renderer error in addLayer:', error);
            this.recreateRenderer();
        }
    }

    getProjection = () => {
        return this.map.getProjection();
    }

    getContainer = () => {
        return this.stage;
    }

    getRenderer = () => {
        return this.renderer;
    }

    clear = () => {
        if (!this.stage) return;
        
        for (let i = this.stage.children.length - 1; i >= 0; i--) {
            this.stage.removeChild(this.stage.children[i]);
        }
        this.rerender();
    }

    setMap = (map) => {
        this.map = map;
    }

    rerender = () => {
        if (this.renderer && this.stage && !this.renderer.destroyed) {
            try {
                this.renderer.render(this.stage);
            } catch (error) {
                console.warn('PIXI renderer error in rerender:', error);
                this.recreateRenderer();
            }
        }
    }
    
    fromPixelToLatLng(pixel) {
        var scale = Math.pow(2, this.map.getZoom());
        var proj = this.map.getProjection();
        var bounds = this.map.getBounds();
      
        var nw = proj.fromLatLngToPoint(
          new google.maps.LatLng(
            bounds.getNorthEast().lat(),
            bounds.getSouthWest().lng()
          ));
        var point = new google.maps.Point(0, 0);
      
        point.x = pixel.x / scale * this.options.resolutionScale + nw.x;
        point.y = pixel.y / scale * this.options.resolutionScale + nw.y;
      
        return proj.fromPointToLatLng(point);
      }

    recreateRenderer = () => {
        try {
            // Destroy existing renderer if it exists
            if (this.renderer && !this.renderer.destroyed) {
                this.renderer.destroy();
            }
            
            // Recreate renderer with the same options
            this.renderer = createOverlayRenderer({
                width: this.canvasLayer.canvas.width,
                height: this.canvasLayer.canvas.height,
                view: this.canvasLayer.canvas,
                antialias: this.options.antialias,
                resolution: this.options.resolution,
                backgroundAlpha: 0
            });
            
            // Re-render the stage
            if (this.stage) {
                this.renderer.render(this.stage);
            }
        } catch (error) {
            console.error('Failed to recreate PIXI renderer:', error);
        }
    }

    fromLatLngToPixel(latLng) {
        var scale = Math.pow(2, this.map.getZoom());
        var proj = this.map.getProjection();
        var bounds = this.map.getBounds();

        var nw = proj.fromLatLngToPoint(
          new google.maps.LatLng(
            bounds.getNorthEast().lat(),
            bounds.getSouthWest().lng()
          ));

        var worldPoint = proj.fromLatLngToPoint(
          new google.maps.LatLng(latLng.lat, latLng.lng)
        );

        return {
          x: (worldPoint.x - nw.x) * scale / this.options.resolutionScale,
          y: (worldPoint.y - nw.y) * scale / this.options.resolutionScale,
        };
    }

    destroy() {
        try {
            if (this.renderer && !this.renderer.destroyed) {
                this.renderer.destroy();
            }
            this.renderer = null;
        } catch (error) {
            console.warn('Error destroying PIXI renderer:', error);
            this.renderer = null;
        }
        
        try {
            if (this.stage) {
                this.stage.destroy({ children: true, texture: true, baseTexture: true });
            }
            this.stage = null;
        } catch (error) {
            console.warn('Error destroying PIXI stage:', error);
            this.stage = null;
        }
        
        try {
            if (this.canvasLayer) {
                // Properly remove the canvas layer from the map which will trigger onRemove() cleanup
                this.canvasLayer.setMap(null);
            }
            this.canvasLayer = null;
        } catch (error) {
            console.warn('Error destroying canvas layer:', error);
            this.canvasLayer = null;
        }
    }
}
